import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import { prepareAnswerWeight } from 'utility/attribute';
import Scrollbars from '../scrollbars';
import ToggleButtonSwitch from '../toggleButtonSwitch';
import GridRow from './gridRow';
import {
  getReviewGridColumns,
  getToggleReportViewItems,
} from '../../../utility/reportUtils';
import { getColumnSizes } from './config';
import { NEUTRAL_ANSWER_WEIGHT } from '../questionEntry/config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    backgroundColor: primary.bluish9,
    boxSizing: 'border-box',
    borderRadius: 8,
    padding: spacing(6),
  },
  gridTable: {
    display: 'flex',
    maxHeight: 538,
    minHeight: 234,
    width: '100%',
  },
  scrollWrapper: {
    width: '100%',
    minHeight: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing(7),
  },
  toggleSwitchWrapper: {
    width: 253,
    marginLeft: 'auto',
  },
  grid: {
    display: 'grid',
    gridColumnGap: 8,
    gridTemplateColumns: getColumnSizes,
    gridTemplateRows: '1fr min-content',
  },
  header: {
    backgroundColor: primary.bluish9,
    display: 'grid',
    position: 'sticky',
    top: 0,
    left: 0,
    zIndex: 100,
    width: '100%',
    '&::after': {
      backgroundColor: primary.bluish9,
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: -8,
      width: '100%',
      height: 8,
    },
  },
  headerCell: {
    backgroundColor: primary.bluish7,
    boxSizing: 'border-box',
    borderRadius: 4,
    padding: spacing(3, 4),
    '&:first-of-type': {
      backgroundColor: primary.bluish9,
      borderRadius: 'initial',
      position: 'sticky',
      left: 0,
      padding: spacing(3, 2, 3, 0),
      '&::after': {
        backgroundColor: primary.bluish9,
        content: '""',
        position: 'absolute',
        top: 0,
        right: -8,
        width: 8,
        height: 38,
      },
    },
  },
  body: {
    position: 'relative',
    zIndex: 99,
    display: 'grid',
    gridRowGap: 8,
    width: '100%',
  },
  scrollY: {
    right: -15,
    '& .os-scrollbar-vertical': {
      paddingTop: spacing(11.5),
    },
  },
  scrollX: {
    bottom: -15,
    '& .os-scrollbar-horizontal': {
      paddingLeft: spacing(52),
    },
  },
}));

const ReportAnswerGrid = ({
  className = undefined,
  translations,
  totalAnswers,
  rows,
  reviewComments = [],
  results,
  isPercentageViewInitial = false,
  hasNeutralAnswer = false,
  hasPercentageView,
  shouldShowQuestionScore = false,
  hideTitle = false,
  attributeLayout,
  onViewQuestionComments = () => {},
}) => {
  const [isPercentageView, setPercentageView] = useState(
    isPercentageViewInitial
  );
  const columns = getReviewGridColumns(
    translations,
    totalAnswers,
    hasNeutralAnswer
  );
  const switchItems = getToggleReportViewItems(translations);
  const hasAverageScore = Boolean(Object.keys(results)?.length);
  const classes = useStyles({ columns });

  const handleChangeView = value => {
    if (value !== isPercentageView) {
      setPercentageView(value);
    }
  };

  const renderHeader = () => (
    <div className={classes.header}>
      <div className={classes.grid}>
        {columns.map((column, index) => (
          <div key={`header_column_${index}`} className={classes.headerCell}>
            <Typography variant="overline">{column.name}</Typography>
          </div>
        ))}
      </div>
    </div>
  );

  const hasOnlyNeutralAnswer = row => {
    if (!results?.[row.id]) {
      return false;
    }

    const allResults = Object.keys(results?.[row.id]);
    const numberOfResults = allResults?.length;
    const singleAnswer = row.answers.find(
      answer => answer.id === parseInt(allResults[0], 10)
    );

    return Boolean(
      numberOfResults > 1 ||
        (numberOfResults === 1 &&
          prepareAnswerWeight(singleAnswer.weight) !== NEUTRAL_ANSWER_WEIGHT)
    );
  };

  const renderBody = () => (
    <div className={classes.body}>
      {rows.map(row => {
        return (
          <GridRow
            key={`row_${row.id}`}
            className={classes.grid}
            translations={translations}
            hasAverageScore={hasAverageScore && hasOnlyNeutralAnswer(row)}
            shouldShowQuestionScore={shouldShowQuestionScore}
            hasNeutralAnswer={hasNeutralAnswer}
            hasPercentageView={hasPercentageView}
            isPercentageView={isPercentageView}
            attributeLayout={attributeLayout}
            row={row}
            result={results?.[row.id]}
            reviewComments={reviewComments}
            onViewQuestionComments={onViewQuestionComments}
          />
        );
      })}
    </div>
  );

  return (
    <div className={classNames(classes.root, className)}>
      {(!hideTitle || hasPercentageView) && (
        <div className={classes.titleWrapper}>
          {!hideTitle && (
            <Typography variant="h4">{translations.answers}</Typography>
          )}
          {hasPercentageView && (
            <div className={classes.toggleSwitchWrapper}>
              <ToggleButtonSwitch
                value={isPercentageView}
                items={switchItems}
                onSelect={handleChangeView}
                isFullWidth
              />
            </div>
          )}
        </div>
      )}
      <div className={classes.gridTable}>
        <div className={classes.scrollWrapper}>
          <Scrollbars
            verticalScrollClass={classes.scrollY}
            horizontalScrollClass={classes.scrollX}
            overlayProps={{
              options: {
                overflow: {
                  x: 'scroll',
                  y: 'scroll',
                },
              },
            }}
          >
            <div className={classes.content}>
              {renderHeader()}
              {renderBody()}
            </div>
          </Scrollbars>
        </div>
      </div>
    </div>
  );
};

ReportAnswerGrid.propTypes = {
  className: PropTypes.string,
  translations: PropTypes.object.isRequired,
  hasNeutralAnswer: PropTypes.bool,
  hasPercentageView: PropTypes.bool.isRequired,
  isPercentageViewInitial: PropTypes.bool,
  shouldShowQuestionScore: PropTypes.bool,
  hideTitle: PropTypes.bool,
  totalAnswers: PropTypes.number.isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  results: PropTypes.shape({}).isRequired,
  reviewComments: PropTypes.arrayOf(PropTypes.object),
  attributeLayout: PropTypes.string.isRequired,
  onViewQuestionComments: PropTypes.func,
};

export default ReportAnswerGrid;
